import { useQuery } from '@tanstack/react-query';
import {
  useEffect,
  useId,
  useRef,
  useState,
  type JSX,
  type RefObject,
} from 'react';

import { Skeleton } from '@/components/ui/skeleton.js';
import {
  diagramPreviewQueryOptions,
  isSupportedKind,
  type DiagramContent,
  type DiagramSummary,
} from '@/lib/api/diagrams.js';
import { cn } from '@/lib/utils.js';

import { DARK_THEME_DRAWING, mermaidFollowsAppTheme } from './drawing-theme.js';
import { drawThumbnail } from './thumbnail-render.js';

/** How far outside the viewport a card starts loading, so it is ready by the time it scrolls in. */
const PRELOAD_MARGIN = '200px';

/**
 * Reports whether the element is within the preload margin of the viewport.
 * Without `IntersectionObserver` it is `true` from the start, so a preview still appears.
 */
function useNearViewport<T extends Element>(): [RefObject<T | null>, boolean] {
  const elementRef = useRef<T | null>(null);
  const [near, setNear] = useState(
    () => typeof IntersectionObserver === 'undefined',
  );
  useEffect(() => {
    const element = elementRef.current;
    if (!element || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        setNear(entries.some((entry) => entry.isIntersecting));
      },
      { rootMargin: PRELOAD_MARGIN },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return [elementRef, near];
}

type DrawingState =
  | { status: 'loading' }
  | { status: 'failed' }
  | { status: 'empty' }
  | { status: 'ready'; svg: string };

/** Draws the loaded document once per content, ignoring a result that arrives after a newer one. */
function useDrawing(content: DiagramContent | undefined): DrawingState {
  const renderId = `thumbnail-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const [drawing, setDrawing] = useState<DrawingState>({ status: 'loading' });
  useEffect(() => {
    if (!content) return;
    let cancelled = false;
    setDrawing({ status: 'loading' });
    drawThumbnail(content, renderId).then(
      (result) => {
        if (!cancelled) setDrawing(result);
      },
      () => {
        if (!cancelled) setDrawing({ status: 'failed' });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [content, renderId]);
  return drawing;
}

function ThumbnailSkeleton(): JSX.Element {
  return (
    <Skeleton
      aria-label="Loading preview"
      className="h-full w-full rounded-none"
    />
  );
}

function ThumbnailMessage({ text }: { text: string }): JSX.Element {
  return (
    <div className="flex h-full w-full items-center justify-center p-4 text-center text-xs text-muted-foreground">
      {text}
    </div>
  );
}

/** Loads the document behind the card and shows its drawing or the reason there is none. */
function ThumbnailContent({
  diagram,
}: {
  diagram: DiagramSummary;
}): JSX.Element {
  const documentQuery = useQuery(
    diagramPreviewQueryOptions(diagram.id, diagram.revision),
  );
  if (documentQuery.isError) {
    return <ThumbnailMessage text="Preview unavailable" />;
  }
  return <ContentDrawing content={documentQuery.data?.content} />;
}

/**
 * A static drawing of one document's content, fitted to its box, or the reason there is none. The
 * gallery card and the revision history both use it, so an old revision looks like its card did.
 */
export function ContentDrawing({
  content,
}: {
  content: DiagramContent | undefined;
}): JSX.Element {
  const drawing = useDrawing(content);
  if (drawing.status === 'failed') {
    return <ThumbnailMessage text="Preview unavailable" />;
  }
  if (!content || drawing.status === 'loading') return <ThumbnailSkeleton />;
  if (drawing.status === 'empty') {
    return (
      <ThumbnailMessage
        text={content.kind === 'canvas' ? 'Empty whiteboard' : 'Empty diagram'}
      />
    );
  }
  const followsAppTheme =
    content.kind !== 'mermaid' || mermaidFollowsAppTheme(content.source);
  return (
    <div
      aria-hidden="true"
      className={cn('h-full w-full p-3', followsAppTheme && DARK_THEME_DRAWING)}
      dangerouslySetInnerHTML={{ __html: drawing.svg }}
    />
  );
}

/**
 * The large preview on a gallery card.
 *
 * Nothing loads until the card is near the viewport, so a big gallery fetches only what somebody
 * can see. The document is cached by id and revision: when the list reports a new revision, the key
 * changes and the preview fetches again. A preview never throws out of its card. Every failure is
 * a quiet line of text in the preview area.
 */
export function DiagramThumbnail({
  diagram,
}: {
  diagram: DiagramSummary;
}): JSX.Element {
  const [frameRef, nearViewport] = useNearViewport<HTMLDivElement>();
  return (
    <div
      ref={frameRef}
      className="aspect-[16/10] w-full overflow-hidden border-b border-border bg-muted/30"
    >
      {!isSupportedKind(diagram.kind) ? (
        <ThumbnailMessage text="Can't preview this kind" />
      ) : nearViewport ? (
        <ThumbnailContent diagram={diagram} />
      ) : (
        <ThumbnailSkeleton />
      )}
    </div>
  );
}
